import React from 'react';
import { Button } from './ui/button';
import { Download, FileSpreadsheet, FileText, Printer } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

/**
 * ExportButtons component - Provides Excel and PDF export functionality
 * 
 * @param {string} module - The module to export (trs, actions, tours, safety)
 * @param {object} filters - Active filters to apply to export
 * @param {string} language - Language code (fr, en, es)
 * @param {boolean} showPrint - Whether to show print button
 */
export const ExportButtons = ({ 
  module, 
  filters = {}, 
  language = 'fr',
  showPrint = true,
  className = ''
}) => {
  const [exporting, setExporting] = React.useState(false);

  const labels = {
    fr: {
      export: 'Exporter',
      excel: 'Excel (.xlsx)',
      pdf: 'PDF',
      print: 'Imprimer',
      exporting: 'Export en cours...',
      success: 'Fichier généré avec succès',
      error: 'Erreur lors de l\'export'
    },
    en: {
      export: 'Export',
      excel: 'Excel (.xlsx)',
      pdf: 'PDF',
      print: 'Print',
      exporting: 'Exporting...',
      success: 'File generated successfully',
      error: 'Export error'
    },
    es: {
      export: 'Exportar',
      excel: 'Excel (.xlsx)',
      pdf: 'PDF',
      print: 'Imprimir',
      exporting: 'Exportando...',
      success: 'Archivo generado con éxito',
      error: 'Error de exportación'
    }
  };

  const t = labels[language] || labels.en;

  const buildQueryString = (format) => {
    const params = new URLSearchParams();
    params.append('language', language);
    
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.site_id) params.append('site_id', filters.site_id);
    if (filters.zone_id) params.append('zone_id', filters.zone_id);
    if (filters.supervisor_id) params.append('supervisor_id', filters.supervisor_id);
    if (filters.shift) params.append('shift', filters.shift);
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    
    return params.toString();
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const queryString = buildQueryString(format);
      const url = `${API}/export/${module}/${format}?${queryString}`;
      
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `GembaCoach_${module}_${new Date().toISOString().split('T')[0]}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/['"]/g, '');
      }
      filename += format === 'excel' ? '.xlsx' : '.pdf';
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(t.success);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t.error);
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = async () => {
    await handleExport('pdf');
    // After downloading PDF, open print dialog
    // The user can print from the downloaded PDF
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            disabled={exporting}
            className="flex items-center gap-2"
            data-testid={`export-btn-${module}`}
          >
            <Download className="w-4 h-4" />
            {exporting ? t.exporting : t.export}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem 
            onClick={() => handleExport('excel')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
            {t.excel}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <FileText className="w-4 h-4 text-red-600" />
            {t.pdf}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showPrint && (
        <Button 
          variant="outline" 
          onClick={handlePrint}
          disabled={exporting}
          className="flex items-center gap-2"
          data-testid={`print-btn-${module}`}
        >
          <Printer className="w-4 h-4" />
          {t.print}
        </Button>
      )}
    </div>
  );
};

/**
 * TourSummaryExport - Export buttons for individual tour summary
 */
export const TourSummaryExport = ({ 
  tourId, 
  language = 'fr',
  className = ''
}) => {
  const [exporting, setExporting] = React.useState(false);

  const labels = {
    fr: { pdf: 'Récapitulatif PDF', excel: 'Export Excel', print: 'Imprimer', success: 'Fichier généré', error: 'Erreur' },
    en: { pdf: 'PDF Summary', excel: 'Export Excel', print: 'Print', success: 'File generated', error: 'Error' },
    es: { pdf: 'Resumen PDF', excel: 'Exportar Excel', print: 'Imprimir', success: 'Archivo generado', error: 'Error' }
  };

  const t = labels[language] || labels.en;

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/export/tour-summary/${tourId}/pdf?language=${language}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `GembaCoach_Tour_${tourId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(t.success);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t.error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button 
        onClick={handleExportPDF}
        disabled={exporting}
        className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
        data-testid="tour-summary-pdf"
      >
        <FileText className="w-4 h-4" />
        {t.pdf}
      </Button>
      <Button 
        variant="outline"
        onClick={handleExportPDF}
        disabled={exporting}
        className="flex items-center gap-2"
        data-testid="tour-summary-print"
      >
        <Printer className="w-4 h-4" />
        {t.print}
      </Button>
    </div>
  );
};

/**
 * CompleteTourExport - Export button for complete tour session (all zones in one PDF)
 * This creates a single PDF with all zones and non-conformity photos
 */
export const CompleteTourExport = ({ 
  tourIds = [], 
  language = 'fr',
  className = ''
}) => {
  const [exporting, setExporting] = React.useState(false);

  const labels = {
    fr: { 
      button: 'Récapitulatif Complet (PDF)', 
      description: 'Toutes les zones en un seul document',
      success: 'Récapitulatif complet généré', 
      error: 'Erreur lors de la génération',
      noTours: 'Aucune tournée à exporter'
    },
    en: { 
      button: 'Complete Summary (PDF)', 
      description: 'All zones in one document',
      success: 'Complete summary generated', 
      error: 'Generation error',
      noTours: 'No tours to export'
    },
    es: { 
      button: 'Resumen Completo (PDF)', 
      description: 'Todas las zonas en un documento',
      success: 'Resumen completo generado', 
      error: 'Error de generación',
      noTours: 'Sin visitas para exportar'
    }
  };

  const t = labels[language] || labels.fr;

  const handleExportComplete = async () => {
    if (!tourIds || tourIds.length === 0) {
      toast.error(t.noTours);
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/export/tour-session/pdf?language=${language}`,
        tourIds,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `GembaCoach_Tournee_Complete_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(t.success);
    } catch (error) {
      console.error('Complete export error:', error);
      toast.error(t.error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={`${className}`}>
      <Button 
        onClick={handleExportComplete}
        disabled={exporting || !tourIds || tourIds.length === 0}
        className="w-full flex items-center justify-center gap-2 bg-[#0E346E] hover:bg-[#0A2850] text-white font-semibold py-3"
        data-testid="complete-tour-export-pdf"
      >
        {exporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            Génération...
          </>
        ) : (
          <>
            <FileText className="w-5 h-5" />
            {t.button}
          </>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center mt-1">{t.description}</p>
    </div>
  );
};

export default ExportButtons;
